import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AccountTree, ArrowBack, FormatListBulleted, Notes } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';

import { DomainStoryRevisionModel, PlanningClient } from '@@api';
import { useApiClient } from '@@common';
import { ViewTitleBar } from '@@components';

import AuthContext from '../../components/AuthContext';
import { ChatMessage } from '../../components/Chat/PromptChatAdapter';
import { isChangeChatMessage } from '../../components/Chat/isChangeChatMessage';
import DomainStoryDiagram from './DomainStoryDiagram';
import DomainStoryOutline from './DomainStoryOutline';
import PlanningChatPanel from './PlanningChatPanel';
import DomainStoryStatements from './DomainStoryStatements';
import { usePlanningChatAdapter } from './usePlanningChatAdapter';

function createEmptyDomainStoryRevision(projectId: string, storyId: string, title?: string, description?: string): DomainStoryRevisionModel {
	return {
		projectId,
		domainStoryId: storyId,
		title: title ?? 'Untitled Domain Story',
		description: description ?? '',
		createdBy: '',
		actors: [],
		workObjects: [],
		steps: [],
		notes: [],
		questions: [],
		potentialStories: [],
	};
}

export default function PlanningStoryDetailView() {
	const { projectId, storyId } = useParams();
	const navigate = useNavigate();
	const client = useApiClient(PlanningClient);
	const queryClient = useQueryClient();
	const auth = useContext(AuthContext);
	const [tab, setTab] = useState<'statements' | 'outline' | 'diagram'>('statements');
	const [chatDraftRequest, setChatDraftRequest] = useState<{ id: string; prompt: string; mode: 'Implement' }>();
	const [selectedRevisionId, setSelectedRevisionId] = useState<string>('latest');
	const chatAdapter = usePlanningChatAdapter(projectId, storyId, auth.isAuthenticated);

	const projectQuery = useQuery({ queryKey: ['planning', 'project', projectId], queryFn: () => client.getProject(projectId!), enabled: !!projectId });
	const storiesQuery = useQuery({
		queryKey: ['planning', 'stories', projectId],
		queryFn: () => client.searchDomainStories(projectId!, undefined, undefined, 0, 100),
		enabled: !!projectId,
	});
	const revisionsQuery = useQuery({ queryKey: ['planning', 'story-revisions', storyId], queryFn: () => client.getDomainStoryRevisions(storyId!), enabled: !!storyId });
	const revisionQuery = useQuery({
		queryKey: ['planning', 'story-revision', storyId, selectedRevisionId],
		queryFn: () => selectedRevisionId === 'latest' ? client.getLatestDomainStoryRevision(storyId!) : client.getDomainStoryRevision(selectedRevisionId),
		enabled: !!storyId && revisionsQuery.isSuccess && revisionsQuery.data.length > 0,
	});
	const statementsQuery = useQuery({ queryKey: ['planning', 'story-statements', storyId], queryFn: () => client.getDomainStoryStatements(storyId!), enabled: !!storyId });

	const orderedRevisions = useMemo(() => [...(revisionsQuery.data ?? [])].sort((left, right) => right.revisionNumber - left.revisionNumber), [revisionsQuery.data]);
	const story = useMemo(() => storiesQuery.data?.items.find((item) => item.id === storyId), [storiesQuery.data, storyId]);
	const emptyRevision = useMemo(() => createEmptyDomainStoryRevision(projectId!, storyId!, story?.title, story?.description), [projectId, storyId, story]);
	const hasRevisions = orderedRevisions.length > 0;
	const handleAssistantMessageReceived = useCallback(async (message: ChatMessage) => {
		if (!isChangeChatMessage(message) || !projectId || !storyId) {
			return;
		}

		setSelectedRevisionId('latest');
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: ['planning', 'stories', projectId], exact: true }),
			queryClient.invalidateQueries({ queryKey: ['planning', 'story-revisions', storyId], exact: true }),
			queryClient.invalidateQueries({ queryKey: ['planning', 'story-revision', storyId] }),
			queryClient.invalidateQueries({ queryKey: ['planning', 'story-statements', storyId] }),
		]);
	}, [projectId, queryClient, storyId]);

	if (revisionsQuery.isLoading || projectQuery.isLoading || storiesQuery.isLoading || statementsQuery.isLoading || (hasRevisions && revisionQuery.isLoading)) {
		return <Box sx={{ display: 'grid', placeItems: 'center', flex: 1 }}><CircularProgress size={28} /></Box>;
	}

	const revision = revisionQuery.data;
	const revisionContent = revision?.revision ?? emptyRevision;
	const statements = statementsQuery.data ?? { domainStoryId: storyId!, statementVersion: 0, statements: [] };
	const diagramIsCurrent = selectedRevisionId === 'latest' && statements.currentRevisionStatementVersion === statements.statementVersion && hasRevisions;
	const diagramStatus = selectedRevisionId !== 'latest' ? 'Historical revision' : !hasRevisions ? 'Not generated' : diagramIsCurrent ? 'Diagram current' : 'Diagram out of date';

	return (
		<Stack sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
			<ViewTitleBar title={revisionContent.title}>
				<Stack direction="row" spacing={1} alignItems="center">
					<Chip label={hasRevisions ? `Revision ${revision?.revisionNumber}` : 'Draft'} size="small" variant="outlined" />
					<FormControl size="small" sx={{ minWidth: 138 }}>
						<InputLabel id="story-revision-label">Version</InputLabel>
						<Select labelId="story-revision-label" label="Version" value={selectedRevisionId} disabled={!hasRevisions} onChange={(event) => setSelectedRevisionId(event.target.value)}>
							<MenuItem value="latest">{hasRevisions ? 'Latest' : 'Draft'}</MenuItem>
							{orderedRevisions.map((item) => <MenuItem key={item.domainStoryRevisionId} value={item.domainStoryRevisionId}>Revision {item.revisionNumber}</MenuItem>)}
						</Select>
					</FormControl>
				</Stack>
			</ViewTitleBar>

			<Stack direction={{ xs: 'column', md: 'row' }} sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
				<PlanningChatPanel adapter={chatAdapter} onAssistantMessageReceived={handleAssistantMessageReceived} storySelected draftRequest={chatDraftRequest} />
				<Stack sx={{ order: { xs: -1, md: 0 }, flex: 1, minWidth: 0, minHeight: { xs: 560, md: 0 } }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 1.5, md: 2 }, py: 1, borderBottom: 1, borderColor: 'divider' }}>
						<Tooltip title="Back to project"><IconButton size="small" onClick={() => navigate(`/planning/projects/${projectId}`)}><ArrowBack /></IconButton></Tooltip>
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="caption" color="text.secondary">{projectQuery.data?.name ?? 'Planning Project'}</Typography>
							<Typography variant="body2" noWrap>{revisionContent.description}</Typography>
						</Box>
					</Box>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', borderBottom: 1, borderColor: 'divider', pr: 1.5 }}>
					<Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ px: 1.5, flex: 1, minWidth: 280 }}>
						<Tab value="statements" icon={<Notes fontSize="small" />} iconPosition="start" label={`Statements (${statements.statements.length})`} />
						<Tab value="outline" icon={<FormatListBulleted fontSize="small" />} iconPosition="start" label="Outline" />
						<Tab value="diagram" icon={<AccountTree fontSize="small" />} iconPosition="start" label="Diagram" />
					</Tabs>
					<Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.75 }}>
						<Chip size="small" label={diagramStatus} color={selectedRevisionId !== 'latest' ? 'default' : diagramIsCurrent ? 'success' : 'warning'} variant="outlined" />
						<Button size="small" variant="outlined" startIcon={<AccountTree />} disabled={statements.statements.length === 0} onClick={() => setChatDraftRequest({ id: crypto.randomUUID(), prompt: 'Generate or refresh the domain story diagram from the current saved statements. Preserve valid detail from the latest revision, validate the complete model, save a new revision, and verify it.', mode: 'Implement' })}>Generate diagram</Button>
					</Stack>
					</Box>
					<Box sx={{ flex: 1, minHeight: 0, overflow: tab === 'diagram' ? 'hidden' : 'auto' }}>
						{tab === 'statements' && <DomainStoryStatements client={client} storyId={storyId!} data={statements} onChanged={(data) => queryClient.setQueryData(['planning', 'story-statements', storyId], data)} />}
						{tab === 'outline' && <DomainStoryOutline revision={revisionContent} />}
						{tab === 'diagram' && <DomainStoryDiagram revision={revisionContent} />}
					</Box>
				</Stack>
			</Stack>
		</Stack>
	);
}
